import { ajvResolver } from '@hookform/resolvers/ajv'
import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IStateDTO } from 'data/dtos/common/i-state-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import StateSchema, { IStateFormData } from 'schema/common/state'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const StateForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const [countriesA, setCountriesA] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IStateFormData>({
    resolver: ajvResolver(StateSchema),
    defaultValues: {
      countryId: '',
      name: '',
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/states/${id}`)
        .then((response) => {
          const { data } = response.data

          const stateResult = {
            countryId: data.countryId.id,
            name: data.name,
          }

          return stateResult
        })
        .then((stateResult: IStateDTO) => {
          reset(stateResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: IStateDTO) => {
    setIsLoading(true)

    const payLoad: IStateDTO = {
      countryId: data.countryId,
      name: data.name,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/states`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso', 'success')
          history.push('/states')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/states', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Estados'
        subTitle='Aqui são informados dados sobre unidades da federação.'
      />

      <Form.Body>
        <Form.Select
          label='País'
          error={!!errors.countryId}
          errorMessage={errors?.countryId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          name='countryId'
          rules={{
            onChange: (e) => {
              setValue('countryId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().countryId}`}
          options={countriesA}
        />

        <Form.Input
          label='Estado'
          error={!!errors.name}
          errorMessage={errors?.name?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='name'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default StateForm
